import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import CaseStatusDropdown from './CaseStatusDropdown';
import {getParticipantName, getParticipantRns} from '../communication/communicationUtils';

const CaseTitle = (props) => {
  const {
    caseData,
    participantDetails,
    updateStatus,
  } = props;

  const [status, setStatus] = React.useState(caseData.status);
  const createdAtDate = new Date(caseData.createdAt);
  const updatedAtDate = new Date(caseData.updatedAt);
  const createdAtLabel = Number.isNaN(createdAtDate.getTime()) ?
    '' :
    createdAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const updatedAtLabel = Number.isNaN(updatedAtDate.getTime()) ?
    '' :
    updatedAtDate.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  const participantNames = getParticipantRns(caseData).map((participantRn) => {
    return getParticipantName(participantDetails, participantRn);
  });
  const detailSections = [
    {key: 'caseRn', label: translate('case.Detail.Fields.CaseId'), value: caseData.caseRn},
    {key: 'participants', label: translate('case.Detail.Fields.Participants'), value: participantNames.join(', ') || translate('case.Detail.EmptyValue')},
    {key: 'status', label: translate('case.Detail.Fields.Status'), type: 'statusDropdown'},
    {key: 'createdAt', label: translate('case.Detail.Fields.Created'), value: createdAtLabel},
    {key: 'updatedAt', label: translate('case.Detail.Fields.Updated'), value: updatedAtLabel},
  ];

  React.useEffect(() => {
    setStatus(caseData.status);
  }, [caseData.status]);

  const handleSetStatus = (selectedStatus) => {
    if (!selectedStatus || selectedStatus === status) {
      return;
    }

    setStatus(selectedStatus);
    updateStatus(selectedStatus);
  };

  return (
    <Stack gap={2}>
      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: '34px',
          color: '#050A14',
        }}
      >
        {caseData.caseType}
      </Typography>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #ECECF1',
          borderRadius: '11px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '1px',
            backgroundColor: '#ECECF1',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, auto))',
              sm: 'repeat(2, minmax(0, auto))',
              md: 'minmax(0, 1.45fr) minmax(0, 1.45fr) minmax(0, 1fr)',
            },
          }}
        >
          {
            detailSections.map((section) => (
              <Box
                key={section.key}
                sx={{
                  minWidth: 0,
                  minHeight: '62px',
                  backgroundColor: '#FFFFFF',
                  px: 2.25,
                  py: 1.75,
                }}
              >
                {
                  section.type === 'statusDropdown' &&
                  <CaseStatusDropdown
                    label={section.label}
                    selectedStatus={status}
                    setSelectedStatus={handleSetStatus}
                  />
                }
                {
                  !section.type &&
                  <Stack
                    sx={{
                      gap: '6px',
                      minWidth: 0,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '11px',
                        lineHeight: '14px',
                        color: '#9A9BAE',
                      }}
                    >
                      {section.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#050A14',
                        wordBreak: 'break-word',
                      }}
                    >
                      {section.value}
                    </Typography>
                  </Stack>
                }
              </Box>
            ))
          }
        </Box>
      </Box>
    </Stack>
  );
};

CaseTitle.propTypes = {
  caseData: PropTypes.object.isRequired,
  participantDetails: PropTypes.object.isRequired,
  updateStatus: PropTypes.func.isRequired,
};

export default React.memo(CaseTitle);
